import { reais } from '../../../lib/dinheiro'
import { competenciaPorExtenso, periodoDaCompetencia } from '../../chamados/competencia'
import type { ApuracaoNaTela } from '../../../types/chamado'
import { QuadroDeIndicadores } from './QuadroDeIndicadores'
import { RessalvaDoTrp } from './RessalvaDoTrp'
import { RodapeDoTrp } from './RodapeDoTrp'
import { TRP } from './textoDoTrp'

/**
 * O Termo de Recebimento Provisório de uma competência, em duas folhas.
 *
 * **Sai só de competência FECHADA, e sempre do snapshot.** O servidor já garante isso: em
 * competência fechada, `GET /api/v1/fiscalizacao/{c}` devolve a apuração congelada, e duas chamadas
 * seguidas dão exatamente o mesmo JSON. Emitir sobre a prévia produziria um termo que muda de
 * número entre a impressão e a assinatura.
 *
 * **A redação é a do TRP nº 01/SSTD/RGG/2026**, transcrita. O que difere do modelo assinado é
 * deliberado e está em dois lugares: a coluna "Nível" traz o índice apurado em vez do texto de
 * gabarito (ver `nivelApurado`), e os seis indicadores ocupam o quadro em vez de quatro.
 *
 * @param props a apuração congelada e o instante da emissão
 * @returns o documento
 */
export function DocumentoTrp({ apuracao, emitidoEm }: { apuracao: ApuracaoNaTela; emitidoEm: string }) {
  const competencia = apuracao.competencia.competencia
  const redutor = apuracao.apuracao.redutor
  const codigo = `TRP-${competencia}-v${apuracao.competencia.versao}`

  return (
    <article className="trp-documento">
      <section className="trp-folha">
        <h1 className="trp-titulo">{TRP.titulo}</h1>
        <p className="trp-subtitulo">
          {TRP.contrato} · Competência {competenciaPorExtenso(competencia)}
        </p>

        <Identificacao competencia={competencia} />

        <h2 className="trp-secao">{TRP.secoes.introducao}</h2>
        <p className="trp-paragrafo">1. {TRP.introducao(periodoDaCompetencia(competencia))}</p>
        <p className="trp-paragrafo">2. {TRP.etapas}</p>

        <h2 className="trp-secao">{TRP.secoes.analise}</h2>
        <p className="trp-paragrafo">
          3. Foram julgados pela fiscalização técnica {apuracao.apuracao.contagens.chamados} chamado(s)
          fechado(s) na competência. O detalhamento de cada julgamento está registrado no Sistema de Gestão.
        </p>

        <RessalvaDoTrp semValorRegistrado={apuracao.glosa == null} />

        <RodapeDoTrp codigo={codigo} emitidoEm={emitidoEm} pagina={1} />
      </section>

      <section className="trp-folha">
        <h2 className="trp-secao">{TRP.secoes.niveis}</h2>
        <p className="trp-paragrafo">4. {TRP.fundamentoDosIndicadores}</p>
        <QuadroDeIndicadores indicadores={apuracao.apuracao.indicadores} />
        <p className="trp-paragrafo" style={{ marginTop: '10px' }}>
          5. {TRP.instrumentos}
        </p>

        <h2 className="trp-secao">{TRP.secoes.ocorrencias}</h2>
        <p className="trp-paragrafo">6. {ocorrenciasDoPeriodo(apuracao)}</p>

        <h2 className="trp-secao">{TRP.secoes.conclusao}</h2>
        <p className="trp-paragrafo">7. {TRP.conclusao}</p>
        <Redutor redutor={redutor} />
        <QuadroDeRecebimento glosa={apuracao.glosa} />

        <RessalvaDoTrp semValorRegistrado={apuracao.glosa == null} />

        <div className="trp-assinatura">
          <p>Niterói, {emitidoEm}.</p>
          <div className="trp-assinatura-linha" />
          <p>{TRP.assinatura.fiscal}</p>
          <p style={{ fontSize: '9pt' }}>{TRP.assinatura.aviso}</p>
        </div>

        <RodapeDoTrp codigo={codigo} emitidoEm={emitidoEm} pagina={2} />
      </section>
    </article>
  )
}

/**
 * A seção IV: o registro de ocorrências.
 *
 * **Deixou de ser texto fixo.** No TRP nº 01 esta seção afirma que "não houve notificações emitidas"
 * — e afirmava isso porque não havia onde registrar ocorrência administrativa. Desde a V27 há: as
 * ocorrências do IAD são o registro, e o quadro do indicador as carrega. Afirmar conformidade
 * administrativa num termo assinado, sem olhar o que está registrado, é o tipo de frase que o
 * controle externo confere primeiro.
 *
 * @param apuracao a apuração congelada da competência
 * @returns o parágrafo da seção IV
 */
function ocorrenciasDoPeriodo(apuracao: ApuracaoNaTela): string {
  const iad = apuracao.apuracao.indicadores.find((i) => i.codigo === 'IAD')
  const ocorrencias = iad?.ocorrencias ?? []
  if (ocorrencias.length === 0) {
    return TRP.semOcorrencias
  }
  const pontos = ocorrencias.reduce((soma, o) => soma + o.pontos, 0)
  return TRP.comOcorrencias(ocorrencias.length, pontos)
}

/** O cabeçalho de identificação do contrato — texto fixo do modelo. */
function Identificacao({ competencia }: { competencia: string }) {
  return (
    <table className="trp-tabela">
      <caption className="sr-only">Identificação do contrato e do período</caption>
      <tbody>
        <Linha rotulo="Objeto" valor={TRP.objeto} />
        <Linha rotulo="Contratante" valor={`${TRP.contratante} — CNPJ ${TRP.cnpjContratante}`} />
        <Linha rotulo="Contratada" valor={`${TRP.contratada} — CNPJ ${TRP.cnpjContratada}`} />
        <Linha rotulo="Processo" valor={TRP.processo} />
        <Linha rotulo="Período de Referência" valor={periodoDaCompetencia(competencia)} />
      </tbody>
    </table>
  )
}

/** Uma linha rótulo/valor da identificação. */
function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <tr>
      <th scope="row" style={{ width: '28%' }}>
        {rotulo}
      </th>
      <td>{valor}</td>
    </tr>
  )
}

/**
 * O quadro de recebimento da seção V — a única parte do termo que fala em reais.
 *
 * **Só existe quando o valor faturado está registrado.** Sem ele, a folha imprime o percentual e a
 * ressalva explica o que falta: um quadro com células vazias seria lido como "nada a descontar", e o
 * que há é "ninguém disse sobre quanto".
 *
 * @param props a glosa em reais, ou nula
 * @returns o quadro, ou nada
 */
function QuadroDeRecebimento({ glosa }: { glosa: ApuracaoNaTela['glosa'] }) {
  if (!glosa) {
    return null
  }
  return (
    <>
      <table className="trp-tabela">
        <caption className="sr-only">Recebimento</caption>
        <tbody>
          <Linha rotulo="Valor faturado" valor={reais(glosa.valorDaFatura) ?? ''} />
          <Linha rotulo="Glosa apurada" valor={reais(glosa.glosaApurada) ?? ''} />
          <Linha rotulo="Glosa aplicada" valor={reais(glosa.glosaAplicada) ?? ''} />
          <Linha rotulo="Valor final a pagar" valor={reais(glosa.valorFinal) ?? ''} />
        </tbody>
      </table>
      <p className="trp-paragrafo" style={{ marginTop: '10px' }}>
        9. O valor faturado tem por fundamento: {glosa.fundamento}
      </p>
    </>
  )
}

/** O desconto apurado, em percentual — os reais, quando há, vêm no quadro acima. */
function Redutor({ redutor }: { redutor: ApuracaoNaTela['apuracao']['redutor'] }) {
  return (
    <p className="trp-paragrafo">
      8. Em razão dos indicadores apurados, a soma dos redutores é de {redutor.somaDosRedutores}%
      {redutor.tetoAlcancado && `, limitada ao teto de ${redutor.tetoPercentual}% do item h (p. 148)`}, do que
      resulta uma redução apurada de <strong>{redutor.redutorApurado}%</strong> sobre o valor da nota fiscal do
      serviço
      {redutor.sancoesDispensadas
        ? `. Registrada a dispensa de sanções do período de ajuste (ANEXO VIII, item d, p. 148), a redução aplicada é de ${redutor.redutorAplicado}%.`
        : '.'}
      {redutor.naoApurados.length > 0 &&
        ` Ficaram sem apuração nesta competência: ${redutor.naoApurados.join(', ')} — e a ausência de número não equivale a conformidade.`}
    </p>
  )
}
